'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminClimbsApi } from '@/lib/adminApi';
import { type Climb } from '@/lib/climbData';
import Icon from '@/components/ui/AppIcon';
import Modal from '@/components/ui/Modal';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ClimbFormData {
  name: string;
  location: string;
  distance: string;
  elevation: string;
  avgGradient: string;
  maxGradient: string;
  difficulty: 'easy' | 'medium' | 'hard' | 'extreme';
  stravaSegmentId: string;
}

const DIFFICULTY_LABEL: Record<string, string> = {
  easy: 'Facile',
  medium: 'Media',
  hard: 'Difficile',
  extreme: 'Estrema',
};

const DIFFICULTY_CLASS: Record<string, string> = {
  easy: 'climb-difficulty-easy',
  medium: 'climb-difficulty-medium',
  hard: 'climb-difficulty-hard',
  extreme: 'climb-difficulty-extreme',
};

// ─── GPX helpers ─────────────────────────────────────────────────────────────

interface GpxPoint { lat: number; lon: number; ele?: number; }
interface GpxData {
  name: string; points: GpxPoint[];
  distanceKm: number; elevationGainM: number;
  avgGradient: number; maxGradient: number;
  startCoords: { lat: number; lon: number };
  endCoords: { lat: number; lon: number };
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function parseGpx(xmlText: string): GpxData | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'application/xml');
    if (doc.querySelector('parsererror')) return null;
    const nameEl = doc.querySelector('trk > name') || doc.querySelector('name');
    const name = nameEl?.textContent?.trim() ?? '';
    const trkpts = Array.from(doc.querySelectorAll('trkpt'));
    if (trkpts.length < 2) return null;
    const points: GpxPoint[] = trkpts.map((pt) => ({
      lat: parseFloat(pt.getAttribute('lat') ?? '0'),
      lon: parseFloat(pt.getAttribute('lon') ?? '0'),
      ele: pt.querySelector('ele') ? parseFloat(pt.querySelector('ele')!.textContent ?? '0') : undefined,
    }));
    let distanceKm = 0;
    let elevationGainM = 0;
    let maxGradient = 0;
    for (let i = 1; i < points.length; i++) {
      const segDist = haversineKm(points[i - 1].lat, points[i - 1].lon, points[i].lat, points[i].lon);
      distanceKm += segDist;
      const elePrev = points[i - 1].ele ?? 0;
      const eleCurr = points[i].ele ?? 0;
      const diff = eleCurr - elePrev;
      if (diff > 0) elevationGainM += diff;
      if (segDist * 1000 > 5) {
        const grad = (diff / (segDist * 1000)) * 100;
        if (grad > maxGradient) maxGradient = grad;
      }
    }
    const avgGradient = distanceKm > 0 ? (elevationGainM / (distanceKm * 1000)) * 100 : 0;
    return {
      name,
      points,
      distanceKm: Math.round(distanceKm * 10) / 10,
      elevationGainM: Math.round(elevationGainM),
      avgGradient: Math.round(avgGradient * 10) / 10,
      maxGradient: Math.round(maxGradient * 10) / 10,
      startCoords: { lat: points[0].lat, lon: points[0].lon },
      endCoords: { lat: points[points.length - 1].lat, lon: points[points.length - 1].lon },
    };
  } catch { return null; }
}

// ─── GPX Upload Zone ──────────────────────────────────────────────────────────

function GpxUploadZone({ onParsed }: { onParsed: (data: GpxData) => void }) {
  const [dragging, setDragging] = useState(false);
  const [status, setStatus] = useState<'idle' | 'parsing' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback((file: File) => {
    if (!file.name.toLowerCase().endsWith('.gpx')) {
      setStatus('error'); setErrorMsg('Formato non supportato. Carica un file .gpx'); return;
    }
    setStatus('parsing');
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = parseGpx(e.target?.result as string);
      if (!data) { setStatus('error'); setErrorMsg('File GPX non valido o senza tracciato'); return; }
      setStatus('success'); onParsed(data);
    };
    reader.onerror = () => { setStatus('error'); setErrorMsg('Errore nella lettura del file'); };
    reader.readAsText(file);
  }, [onParsed]);

  return (
    <div className="mb-5">
      <label className="block text-sm font-semibold text-foreground mb-2">Importa da GPX (opzionale)</label>
      {status !== 'success' ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) processFile(f); }}
          onClick={() => inputRef.current?.click()}
          className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-5 cursor-pointer transition-all ${dragging ? 'border-primary bg-primary/10' : status === 'error' ? 'border-red-400/50 bg-red-400/5' : 'border-border bg-muted/20 hover:border-primary/50'}`}
        >
          <input ref={inputRef} type="file" accept=".gpx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); }} />
          {status === 'parsing' ? <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /> :
           status === 'error' ? <p className="text-sm text-red-400">{errorMsg}</p> :
           <p className="text-sm text-muted-foreground">Trascina un file .gpx o clicca per selezionarlo</p>}
        </div>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/30">
          <Icon name="CheckCircleIcon" size={14} className="text-primary" />
          <span className="text-sm text-primary font-medium">GPX importato</span>
          <button type="button" onClick={() => setStatus('idle')} className="ml-auto text-xs text-muted-foreground hover:text-foreground">Rimuovi</button>
        </div>
      )}
    </div>
  );
}

// ─── Climb Form ───────────────────────────────────────────────────────────────

interface ClimbFormProps {
  initial?: Partial<ClimbFormData>;
  onSubmit: (data: ClimbFormData) => void;
  onCancel: () => void;
  submitLabel: string;
  saving: boolean;
  onGpxParsed?: (data: GpxData) => void;
}

function ClimbForm({ initial, onSubmit, onCancel, submitLabel, saving, onGpxParsed }: ClimbFormProps) {
  const [form, setForm] = useState<ClimbFormData>({
    name: initial?.name ?? '',
    location: initial?.location ?? '',
    distance: initial?.distance ?? '',
    elevation: initial?.elevation ?? '',
    avgGradient: initial?.avgGradient ?? '',
    maxGradient: initial?.maxGradient ?? '',
    difficulty: initial?.difficulty ?? 'hard',
    stravaSegmentId: initial?.stravaSegmentId ?? '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ClimbFormData, string>>>({});

  const set = (k: keyof ClimbFormData, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const validate = (): boolean => {
    const e: Partial<Record<keyof ClimbFormData, string>> = {};
    if (!form.name.trim()) e.name = 'Nome obbligatorio';
    if (!form.location.trim()) e.location = 'Regione obbligatoria';
    if (!form.distance || isNaN(parseFloat(form.distance))) e.distance = 'Distanza non valida';
    if (!form.elevation || isNaN(parseInt(form.elevation))) e.elevation = 'Dislivello non valido';
    if (!form.avgGradient || isNaN(parseFloat(form.avgGradient))) e.avgGradient = 'Pendenza media non valida';
    if (!form.maxGradient || isNaN(parseFloat(form.maxGradient))) e.maxGradient = 'Pendenza max non valida';
    if (!form.stravaSegmentId.trim()) e.stravaSegmentId = 'ID segmento obbligatorio';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleGpx = (data: GpxData) => {
    setForm((p) => ({
      ...p,
      name: data.name || p.name,
      distance: data.distanceKm.toString(),
      elevation: data.elevationGainM.toString(),
      avgGradient: data.avgGradient.toString(),
      maxGradient: data.maxGradient.toString(),
    }));
    onGpxParsed?.(data);
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (validate()) onSubmit(form); }}>
      {onGpxParsed !== undefined && <GpxUploadZone onParsed={handleGpx} />}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-semibold text-foreground mb-1.5">Nome salita</label>
            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} className="input-field w-full px-4 py-2.5 text-sm" placeholder="Passo dello Stelvio" />
            {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Regione</label>
            <input type="text" value={form.location} onChange={(e) => set('location', e.target.value)} className="input-field w-full px-4 py-2.5 text-sm" placeholder="Alto Adige" />
            {errors.location && <p className="mt-1 text-xs text-red-400">{errors.location}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Difficoltà</label>
            <select value={form.difficulty} onChange={(e) => set('difficulty', e.target.value as ClimbFormData['difficulty'])} className="input-field w-full px-4 py-2.5 text-sm">
              <option value="easy">Facile</option>
              <option value="medium">Media</option>
              <option value="hard">Difficile</option>
              <option value="extreme">Estrema</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Distanza (km)</label>
            <input type="number" step="0.1" value={form.distance} onChange={(e) => set('distance', e.target.value)} className="input-field w-full px-4 py-2.5 text-sm" placeholder="24.3" />
            {errors.distance && <p className="mt-1 text-xs text-red-400">{errors.distance}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Dislivello (m)</label>
            <input type="number" value={form.elevation} onChange={(e) => set('elevation', e.target.value)} className="input-field w-full px-4 py-2.5 text-sm" placeholder="1808" />
            {errors.elevation && <p className="mt-1 text-xs text-red-400">{errors.elevation}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Pendenza media (%)</label>
            <input type="number" step="0.1" value={form.avgGradient} onChange={(e) => set('avgGradient', e.target.value)} className="input-field w-full px-4 py-2.5 text-sm" placeholder="7.4" />
            {errors.avgGradient && <p className="mt-1 text-xs text-red-400">{errors.avgGradient}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Pendenza max (%)</label>
            <input type="number" step="0.1" value={form.maxGradient} onChange={(e) => set('maxGradient', e.target.value)} className="input-field w-full px-4 py-2.5 text-sm" placeholder="14.0" />
            {errors.maxGradient && <p className="mt-1 text-xs text-red-400">{errors.maxGradient}</p>}
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-semibold text-foreground mb-1.5">ID Segmento Strava</label>
            <p className="text-xs text-muted-foreground mb-1.5">URL: strava.com/segments/<strong>629609</strong></p>
            <input type="text" value={form.stravaSegmentId} onChange={(e) => set('stravaSegmentId', e.target.value)} className="input-field w-full px-4 py-2.5 text-sm font-mono" placeholder="629609" />
            {errors.stravaSegmentId && <p className="mt-1 text-xs text-red-400">{errors.stravaSegmentId}</p>}
          </div>
        </div>
      </div>
      <div className="flex gap-3 justify-end mt-6">
        <button type="button" onClick={onCancel} className="btn-ghost px-4 py-2 rounded-lg text-sm font-semibold" disabled={saving}>Annulla</button>
        <button type="submit" className="btn-primary px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2" disabled={saving}>
          {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

// ─── Row mapper ───────────────────────────────────────────────────────────────

function rowToClimb(row: any): Climb {
  return {
    id: row.id,
    name: row.name,
    location: row.location,
    distance: Number(row.distance),
    elevation: row.elevation,
    avgGradient: Number(row.avg_gradient),
    maxGradient: Number(row.max_gradient),
    difficulty: row.difficulty,
    stravaSegmentId: row.strava_segment_id,
    gpxRoute: row.gpx_route ?? undefined,
    startCoords: row.start_coords ?? undefined,
    endCoords: row.end_coords ?? undefined,
  };
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminClimbsTab() {
  const [climbs, setClimbs] = useState<Climb[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Climb | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Climb | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [gpxData, setGpxData] = useState<GpxData | null>(null);

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const loadClimbs = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const { data, error } = await adminClimbsApi.getAll();
    if (error) {
      setLoadError(`Errore caricamento: ${error}`);
      setClimbs([]);
    } else {
      setClimbs((data ?? []).map(rowToClimb));
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadClimbs(); }, [loadClimbs]);

  const handleAdd = async (formData: ClimbFormData) => {
    setSaving(true);
    const newId = `climb-${formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now()}`;
    const row = {
      id: newId,
      name: formData.name.trim(),
      location: formData.location.trim(),
      distance: parseFloat(formData.distance),
      elevation: parseInt(formData.elevation),
      avg_gradient: parseFloat(formData.avgGradient),
      max_gradient: parseFloat(formData.maxGradient),
      difficulty: formData.difficulty,
      strava_segment_id: formData.stravaSegmentId.trim(),
      gpx_route: gpxData?.points ?? null,
      start_coords: gpxData?.startCoords ?? null,
      end_coords: gpxData?.endCoords ?? null,
    };
    const { error } = await adminClimbsApi.insert(row);
    setSaving(false);
    if (error) {
      showToast('error', `Errore aggiunta: ${error}`);
    } else {
      setAddOpen(false);
      setGpxData(null);
      showToast('success', 'Salita aggiunta con successo');
      await loadClimbs();
    }
  };

  const handleEdit = async (formData: ClimbFormData) => {
    if (!editTarget) return;
    setSaving(true);
    const row = {
      name: formData.name.trim(),
      location: formData.location.trim(),
      distance: parseFloat(formData.distance),
      elevation: parseInt(formData.elevation),
      avg_gradient: parseFloat(formData.avgGradient),
      max_gradient: parseFloat(formData.maxGradient),
      difficulty: formData.difficulty,
      strava_segment_id: formData.stravaSegmentId.trim(),
      gpx_route: gpxData?.points ?? editTarget.gpxRoute ?? null,
      start_coords: gpxData?.startCoords ?? editTarget.startCoords ?? null,
      end_coords: gpxData?.endCoords ?? editTarget.endCoords ?? null,
      updated_at: new Date().toISOString(),
    };
    const { error } = await adminClimbsApi.update(editTarget.id, row);
    setSaving(false);
    if (error) {
      showToast('error', `Errore modifica: ${error}`);
    } else {
      setEditTarget(null);
      setGpxData(null);
      showToast('success', 'Salita modificata con successo');
      await loadClimbs();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    const { error } = await adminClimbsApi.delete(deleteTarget.id);
    setSaving(false);
    setDeleteTarget(null);
    if (error) {
      showToast('error', `Errore eliminazione: ${error}`);
    } else {
      showToast('success', 'Salita eliminata');
      await loadClimbs();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Toast */}
      {toast && (
        <div className={`mb-4 px-4 py-3 rounded-lg border text-sm font-semibold flex items-center gap-2 ${toast.type === 'success' ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-red-400/10 border-red-400/30 text-red-400'}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={16} />
          {toast.msg}
        </div>
      )}

      {/* Load error */}
      {loadError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-400/10 border border-red-400/30 text-red-400 text-sm flex items-center justify-between">
          <span className="flex items-center gap-2"><Icon name="ExclamationTriangleIcon" size={16} />{loadError}</span>
          <button onClick={loadClimbs} className="text-xs underline">Riprova</button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-muted-foreground">{climbs.length} salite configurate</p>
        <button onClick={() => { setGpxData(null); setAddOpen(true); }} className="btn-primary px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2">
          <Icon name="PlusIcon" size={16} />
          Aggiungi salita
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {climbs.map((climb) => (
          <div key={`admin-climb-${climb.id}`} className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-all duration-200">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-foreground">{climb.name}</h3>
                <p className="text-xs text-muted-foreground">{climb.location}</p>
              </div>
              <span className={`text-xs font-semibold ${DIFFICULTY_CLASS[climb.difficulty]}`}>
                {DIFFICULTY_LABEL[climb.difficulty]}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { label: 'Distanza', value: `${climb.distance} km` },
                { label: 'Dislivello', value: `+${climb.elevation.toLocaleString('it')} m` },
                { label: 'Pend. media', value: `${climb.avgGradient}%` },
                { label: 'Pend. max', value: `${climb.maxGradient}%` },
              ].map((stat) => (
                <div key={`stat-${climb.id}-${stat.label}`} className="bg-muted/50 rounded-lg px-3 py-2">
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                  <p className="text-sm font-bold text-foreground">{stat.value}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Segmento: #{climb.stravaSegmentId}</span>
              <div className="flex gap-1">
                <button
                  onClick={() => { setGpxData(null); setEditTarget(climb); }}
                  className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  title="Modifica"
                >
                  <Icon name="PencilIcon" size={14} />
                </button>
                <button
                  onClick={() => setDeleteTarget(climb)}
                  className="p-1.5 rounded-lg bg-red-400/10 text-red-400 hover:bg-red-400/20 transition-colors"
                  title="Elimina"
                >
                  <Icon name="TrashIcon" size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {climbs.length === 0 && !loadError && (
        <div className="text-center py-16">
          <Icon name="ArrowTrendingUpIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">Nessuna salita configurata</p>
        </div>
      )}

      {/* Add Modal */}
      <Modal open={addOpen} onClose={() => { setAddOpen(false); setGpxData(null); }} title="Aggiungi nuova salita" maxWidth="max-w-xl">
        <ClimbForm
          onSubmit={handleAdd}
          onCancel={() => { setAddOpen(false); setGpxData(null); }}
          submitLabel="Aggiungi salita"
          saving={saving}
          onGpxParsed={setGpxData}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editTarget} onClose={() => { setEditTarget(null); setGpxData(null); }} title={`Modifica: ${editTarget?.name ?? ''}`} maxWidth="max-w-xl">
        {editTarget && (
          <ClimbForm
            initial={{
              name: editTarget.name,
              location: editTarget.location,
              distance: editTarget.distance.toString(),
              elevation: editTarget.elevation.toString(),
              avgGradient: editTarget.avgGradient.toString(),
              maxGradient: editTarget.maxGradient.toString(),
              difficulty: editTarget.difficulty,
              stravaSegmentId: editTarget.stravaSegmentId,
            }}
            onSubmit={handleEdit}
            onCancel={() => { setEditTarget(null); setGpxData(null); }}
            submitLabel="Salva modifiche"
            saving={saving}
            onGpxParsed={setGpxData}
          />
        )}
      </Modal>

      {/* Delete Modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Elimina salita">
        <p className="text-sm text-muted-foreground mb-5">
          Stai per eliminare <span className="font-semibold text-foreground">{deleteTarget?.name}</span> e tutti i tempi associati. Questa azione non può essere annullata.
        </p>
        <div className="flex gap-3 justify-end">
          <button onClick={() => setDeleteTarget(null)} className="btn-ghost px-4 py-2 rounded-lg text-sm font-semibold" disabled={saving}>Annulla</button>
          <button
            onClick={handleDelete}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-400/10 text-red-400 hover:bg-red-400/20 border border-red-400/30 transition-colors flex items-center gap-2"
          >
            {saving && <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />}
            Elimina definitivamente
          </button>
        </div>
      </Modal>
    </div>
  );
}